import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Users, CalendarDays, BadgeCheck, Quote } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { CountUp } from "../components/CountUp";
import { CTASection } from "../components/CTASection";
import { IMAGES, IMPACT_AREAS, PROGRAMS, PROJECTS, STATS, ACTIVITIES, GALLERY, STORIES } from "../data/content";

const Hero = () => (
  <section className="relative min-h-[92vh] flex flex-col" data-testid="hero-section">
    <div className="absolute inset-0">
      <img src={IMAGES.hero} alt="HECSO health workers supporting a community in Assam" className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/55 to-black/30" />
    </div>
    <div className="relative container-hecso flex-1 flex flex-col justify-center py-24">
      <Reveal>
        <p className="max-w-2xl text-base sm:text-lg text-white/85 leading-relaxed">
          Supporting families across Assam through healthcare, education, awareness and community development.
        </p>
        <div className="mt-8 flex flex-wrap gap-4">
          <Link
            to="/our-work"
            data-testid="hero-explore-btn"
            className="inline-flex items-center gap-2 rounded-lg bg-hecso-green px-6 py-3 text-sm font-semibold text-white transition-all hover:bg-hecso-greendark hover:-translate-y-0.5 active:scale-95"
          >
            Explore Our Work <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/donate"
            data-testid="hero-support-btn"
            className="inline-flex items-center gap-2 rounded-lg bg-hecso-orange px-6 py-3 text-sm font-semibold text-white transition-all hover:bg-hecso-orangehover hover:-translate-y-0.5 active:scale-95"
          >
            Support Our Mission
          </Link>
        </div>
      </Reveal>
    </div>
    <div className="relative bg-hecso-greendark/90 backdrop-blur-sm">
      <div className="container-hecso grid grid-cols-2 lg:grid-cols-4 gap-6 py-7">
        {[
          { value: 20, suffix: "+", label: "Years of Service" },
          { value: 50000, suffix: "+", label: "Lives Impacted" },
          { value: 100, suffix: "+", label: "Community Programs" },
        ].map((s) => (
          <div key={s.label} data-testid={`hero-stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <p className="font-heading text-2xl sm:text-3xl font-bold text-white">
              <CountUp end={s.value} suffix={s.suffix} />
            </p>
            <p className="mt-1 text-xs sm:text-sm text-white/70">{s.label}</p>
          </div>
        ))}
        <div data-testid="hero-stat-location">
          <p className="font-heading text-2xl sm:text-3xl font-bold text-white">Est. 2004</p>
          <p className="mt-1 text-xs sm:text-sm text-white/70">Assam, India</p>
        </div>
      </div>
    </div>
  </section>
);

const SectionHead = ({ eyebrow, title, description }) => (
  <Reveal className="max-w-3xl mx-auto text-center mb-12">
    <span className="text-xs font-semibold uppercase tracking-widest text-hecso-orange">{eyebrow}</span>
    <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-hecso-ink">{title}</h2>
    {description && <p className="mt-4 text-sm sm:text-base text-hecso-muted leading-relaxed">{description}</p>}
  </Reveal>
);

const ImpactSection = () => (
  <section className="bg-hecso-beige py-16 sm:py-20" data-testid="impact-section">
    <div className="container-hecso">
      <SectionHead
        eyebrow="Our Impact"
        title="Creating Change Where It Matters Most"
        description="HECSO works with local communities, schools, healthcare professionals, volunteers and institutions to improve access to essential services for families across Assam."
      />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {IMPACT_AREAS.map((area, i) => (
          <Reveal key={area.title} delay={i * 100}>
            <div className="group h-full rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm transition-shadow hover:shadow-md" data-testid={`impact-card-${i}`}>
              <div className="h-44 overflow-hidden">
                <img src={area.image} alt={area.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </div>
              <div className="p-5">
                <h3 className="font-heading text-lg font-semibold text-hecso-greendark">{area.title}</h3>
                <p className="mt-2 text-sm text-hecso-muted leading-relaxed">{area.description}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const AboutSection = () => (
  <section className="py-16 sm:py-20 bg-white" data-testid="about-section">
    <div className="container-hecso grid lg:grid-cols-2 gap-12 items-center">
      <Reveal>
        <div className="relative">
          <img src={IMAGES.reliefQueue} alt="HECSO relief distribution during a community program" className="rounded-2xl w-full h-[420px] object-cover shadow-md" />
          <div className="absolute -bottom-6 -right-4 sm:right-6 rounded-xl bg-hecso-green text-white px-6 py-4 shadow-lg">
            <p className="font-heading text-2xl font-bold"><CountUp end={22} suffix="+" /></p>
            <p className="text-xs text-white/80">Years of Community Service</p>
          </div>
        </div>
      </Reveal>
      <Reveal delay={150}>
        <span className="text-xs font-semibold uppercase tracking-widest text-hecso-orange">About HECSO</span>
        <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-hecso-ink">
          More Than Two Decades of Community Service
        </h2>
        <p className="mt-5 text-sm sm:text-base text-hecso-muted leading-relaxed">
          Helpline Health Care Society (HECSO) was established on 18 April 2004 with the objective of improving
          healthcare awareness, education and social welfare within communities across Assam.
        </p>
        <p className="mt-4 text-sm sm:text-base text-hecso-muted leading-relaxed">
          Over the years the organization has conducted health camps, eye screening programs, awareness campaigns,
          educational initiatives, disability support programs and community development projects — reaching more
          than 50,000 people across 15+ districts.
        </p>
        <Link
          to="/about"
          data-testid="about-journey-link"
          className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-hecso-green transition-colors hover:text-hecso-greendark"
        >
          Learn About Our Journey <ArrowRight className="w-4 h-4" />
        </Link>
      </Reveal>
    </div>
  </section>
);

const ProgramsSection = () => (
  <section className="py-16 sm:py-20 bg-hecso-beige" data-testid="programs-section">
    <div className="container-hecso">
      <SectionHead
        eyebrow="What We Do"
        title="Our Programs"
        description="From free health camps to women's skill development, our programs address the most pressing needs of the communities we serve."
      />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {PROGRAMS.slice(0, 6).map((p, i) => (
          <Reveal key={p.slug} delay={(i % 3) * 100}>
            <div className="group h-full rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm transition-shadow hover:shadow-md" data-testid={`program-card-${p.slug}`}>
              <div className="h-48 overflow-hidden">
                <img src={p.image} alt={p.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </div>
              <div className="p-5">
                <h3 className="font-heading text-lg font-semibold text-hecso-ink">{p.title}</h3>
                <p className="mt-2 text-sm text-hecso-muted leading-relaxed">{p.description}</p>
                <Link to="/programs" data-testid={`program-learn-more-${p.slug}`} className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-hecso-green hover:text-hecso-greendark transition-colors">
                  Learn More <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal className="text-center mt-10">
        <Link
          to="/programs"
          data-testid="view-all-programs-btn"
          className="inline-flex items-center gap-2 rounded-lg border-2 border-hecso-green px-6 py-3 text-sm font-semibold text-hecso-green transition-colors hover:bg-hecso-green hover:text-white"
        >
          View All Programs <ArrowRight className="w-4 h-4" />
        </Link>
      </Reveal>
    </div>
  </section>
);

const statusColor = (s) =>
  s === "Ongoing" ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-blue-50 text-blue-800 border-blue-200";

const ProjectsSection = () => (
  <section className="py-16 sm:py-20 bg-white" data-testid="projects-section">
    <div className="container-hecso">
      <SectionHead
        eyebrow="Featured Work"
        title="Projects Making a Difference"
        description="A look at some of our ongoing and completed projects bringing essential services to communities across Assam."
      />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {PROJECTS.slice(0, 3).map((p, i) => (
          <Reveal key={p.title} delay={i * 100}>
            <div className="h-full rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm transition-shadow hover:shadow-md" data-testid={`project-card-${i}`}>
              <div className="relative h-52 overflow-hidden group">
                <img src={p.image} alt={p.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <span className={`absolute top-3 right-3 rounded-full border px-3 py-1 text-xs font-semibold ${statusColor(p.status)}`}>
                  {p.status}
                </span>
              </div>
              <div className="p-6">
                <h3 className="font-heading text-lg font-semibold text-hecso-ink">{p.title}</h3>
                <p className="mt-2 text-sm text-hecso-muted leading-relaxed">{p.description}</p>
                <div className="mt-5 grid grid-cols-2 gap-3 text-xs">
                  <span className="flex items-center gap-1.5 text-hecso-muted"><MapPin className="w-3.5 h-3.5 text-hecso-green" /> {p.location}</span>
                  <span className="flex items-center gap-1.5 text-hecso-muted"><Users className="w-3.5 h-3.5 text-hecso-green" /> {p.beneficiaries}</span>
                  <span className="flex items-center gap-1.5 text-hecso-muted"><CalendarDays className="w-3.5 h-3.5 text-hecso-green" /> {p.year}</span>
                  <span className="flex items-center gap-1.5 text-hecso-muted"><BadgeCheck className="w-3.5 h-3.5 text-hecso-green" /> {p.status}</span>
                </div>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const StatsSection = () => (
  <section className="relative py-16 sm:py-20" data-testid="stats-section">
    <div className="absolute inset-0">
      <img src={IMAGES.teaGarden} alt="" className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-hecso-greendark/92" />
    </div>
    <div className="relative container-hecso">
      <Reveal className="text-center mb-10">
        <h2 className="text-2xl sm:text-3xl font-semibold text-white tracking-tight">Two Decades of Measurable Impact</h2>
      </Reveal>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
        {STATS.map((s, i) => (
          <Reveal key={s.label} delay={i * 80} className="text-center">
            <div data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
              <p className="font-heading text-3xl sm:text-4xl font-bold text-hecso-orange">
                {s.display ? <CountUp end={s.value} suffix={s.suffix} display={s.display} /> : <CountUp end={s.value} suffix={s.suffix} />}
              </p>
              <p className="mt-2 text-xs sm:text-sm text-white/80">{s.label}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const ActivitiesSection = () => (
  <section className="py-16 sm:py-20 bg-hecso-beige" data-testid="activities-section">
    <div className="container-hecso">
      <SectionHead
        eyebrow="News & Updates"
        title="Latest From HECSO"
        description="Recent camps, programs and awareness activities from the field."
      />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {ACTIVITIES.slice(0, 3).map((a, i) => (
          <Reveal key={a.title} delay={i * 100}>
            <article className="group h-full rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm transition-shadow hover:shadow-md" data-testid={`activity-card-${i}`}>
              <div className="h-48 overflow-hidden">
                <img src={a.image} alt={a.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </div>
              <div className="p-5">
                <div className="flex items-center gap-3 text-xs">
                  <span className="text-hecso-muted">{a.date}</span>
                  <span className="rounded-full bg-hecso-greensubtle border border-emerald-200 px-2.5 py-0.5 font-semibold text-hecso-greendark">{a.category}</span>
                </div>
                <h3 className="mt-3 font-heading text-base font-semibold text-hecso-ink leading-snug">{a.title}</h3>
                <p className="mt-2 text-sm text-hecso-muted leading-relaxed line-clamp-3">{a.description}</p>
                <Link to="/events" data-testid={`activity-read-more-${i}`} className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-hecso-green hover:text-hecso-greendark transition-colors">
                  Read More <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </article>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const GalleryStrip = () => (
  <section className="py-16 sm:py-20 bg-white" data-testid="gallery-strip">
    <div className="container-hecso">
      <SectionHead eyebrow="From the Field" title="Moments From Our Work" />
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {[GALLERY[1], GALLERY[2], GALLERY[3], GALLERY[7], GALLERY[16], GALLERY[17]].map((g, i) => (
          <Reveal key={i} delay={i * 60}>
            <Link to="/gallery" className="block group overflow-hidden rounded-lg" data-testid={`gallery-strip-item-${i}`}>
              <img src={g.src} alt={g.caption} className="h-36 w-full object-cover transition-transform duration-500 group-hover:scale-110" />
            </Link>
          </Reveal>
        ))}
      </div>
      <Reveal className="text-center mt-8">
        <Link to="/gallery" data-testid="view-gallery-btn" className="inline-flex items-center gap-2 text-sm font-semibold text-hecso-green hover:text-hecso-greendark transition-colors">
          View Full Gallery <ArrowRight className="w-4 h-4" />
        </Link>
      </Reveal>
    </div>
  </section>
);

const StoriesSection = () => (
  <section className="py-16 sm:py-20 bg-hecso-beige" data-testid="stories-section">
    <div className="container-hecso">
      <SectionHead
        eyebrow="Success Stories"
        title="Stories From Our Communities"
        description="Behind every number is a person. These are a few of the lives touched by our programs."
      />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {STORIES.map((s, i) => (
          <Reveal key={s.name} delay={i * 100}>
            <div className="h-full rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm" data-testid={`story-card-${i}`}>
              <div className="h-52 overflow-hidden">
                <img src={s.image} alt={s.name} className="w-full h-full object-cover" />
              </div>
              <div className="p-6">
                <Quote className="w-6 h-6 text-hecso-orange" />
                <h3 className="mt-3 font-heading text-base font-semibold text-hecso-ink">{s.title}</h3>
                <p className="mt-2 text-sm text-hecso-muted leading-relaxed italic">“{s.quote}”</p>
                <p className="mt-4 text-sm font-semibold text-hecso-greendark">{s.name}</p>
                <p className="text-xs text-hecso-muted">{s.place}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export default function Home() {
  return (
    <main data-testid="home-page">
      <Hero />
      <ImpactSection />
      <AboutSection />
      <ProgramsSection />
      <ProjectsSection />
      <StatsSection />
      <ActivitiesSection />
      <GalleryStrip />
      <StoriesSection />
      <CTASection />
    </main>
  );
}
